import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Coins, Play, ArrowUp, ArrowDown, Check, LinkSimple } from '@phosphor-icons/react';

const CHAINS = [
    {
        id: 1, name: "Outback Food Chain", habitat: "🏜️ Outback",
        items: [
            { name: "Sun", emoji: "☀️", description: "Energy source" },
            { name: "Grass", emoji: "🌾", description: "Producer — grows using sunlight" },
            { name: "Grasshopper", emoji: "🦗", description: "Primary consumer — eats plants" },
            { name: "Lizard", emoji: "🦎", description: "Secondary consumer — eats insects" },
            { name: "Kookaburra", emoji: "🐦", description: "Tertiary consumer — eats lizards" },
            { name: "Wedge-tailed Eagle", emoji: "🦅", description: "Apex predator" },
        ]
    },
    {
        id: 2, name: "Ocean Food Chain", habitat: "🪸 Ocean",
        items: [
            { name: "Sun", emoji: "☀️", description: "Energy source" },
            { name: "Phytoplankton", emoji: "🦠", description: "Producer — tiny ocean plants" },
            { name: "Zooplankton", emoji: "🫧", description: "Primary consumer — tiny animals" },
            { name: "Small Fish", emoji: "🐟", description: "Secondary consumer — eats plankton" },
            { name: "Tuna", emoji: "🐠", description: "Tertiary consumer — eats small fish" },
            { name: "Great White Shark", emoji: "🦈", description: "Apex predator" },
        ]
    },
    {
        id: 3, name: "Rainforest Food Chain", habitat: "🌿 Rainforest",
        items: [
            { name: "Sun", emoji: "☀️", description: "Energy source" },
            { name: "Fig Tree", emoji: "🌳", description: "Producer — fruit and leaves" },
            { name: "Possum", emoji: "🐿️", description: "Primary consumer — eats fruit" },
            { name: "Python", emoji: "🐍", description: "Secondary consumer — eats possums" },
            { name: "Crocodile", emoji: "🐊", description: "Tertiary consumer" },
            { name: "Nothing", emoji: "👑", description: "Apex — no natural predator" },
        ]
    }
];

function shuffle(arr) { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }

export default function FoodChainGame() {
    const { api } = useAuth();
    const [started, setStarted] = useState(false);
    const [chains, setChains] = useState([]);
    const [currentChain, setCurrentChain] = useState(0);
    const [order, setOrder] = useState([]);
    const [checked, setChecked] = useState(false);
    const [scores, setScores] = useState([]);
    const [gameOver, setGameOver] = useState(false);
    const [result, setResult] = useState(null);

    const startGame = () => {
        const selected = shuffle(CHAINS).slice(0, 2);
        setChains(selected);
        setCurrentChain(0);
        setOrder(shuffle([...selected[0].items]));
        setChecked(false); setScores([]); setGameOver(false); setResult(null); setStarted(true);
    };

    const moveItem = (idx, dir) => {
        if (checked) return;
        const newOrder = [...order];
        const swapIdx = idx + dir;
        if (swapIdx < 0 || swapIdx >= newOrder.length) return;
        [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
        setOrder(newOrder);
    };

    const handleCheck = () => {
        const chain = chains[currentChain];
        let correct = 0;
        order.forEach((item, i) => { if (item.name === chain.items[i].name) correct++; });
        setScores(prev => [...prev, correct]);
        setChecked(true);
    };

    const handleNext = async () => {
        if (currentChain + 1 >= chains.length) {
            setGameOver(true);
            const total = [...scores].reduce((s, v) => s + v, 0);
            const max = chains.reduce((s, c) => s + c.items.length, 0);
            try {
                const res = await api().post('/games/complete', {
                    game_id: 'food_chain', score: Math.round((total / max) * 100), difficulty: 'medium'
                });
                setResult(res.data);
            } catch (err) { console.error('Failed'); }
        } else {
            const nextIdx = currentChain + 1;
            setCurrentChain(nextIdx);
            setOrder(shuffle([...chains[nextIdx].items]));
            setChecked(false);
        }
    };

    if (!started) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
                <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-20">
                            <Link to="/games" className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full bg-[#ef4444] border-4 border-[#1e293b] flex items-center justify-center">
                                    <LinkSimple weight="fill" className="text-white" size={20} />
                                </div>
                                <span className="font-fredoka text-xl font-bold text-[#1e293b]">Food Chain Puzzle</span>
                            </Link>
                            <Link to="/games" className="brutalist-btn bg-white text-[#1e293b] text-sm flex items-center gap-1.5"><ArrowLeft weight="bold" size={16} /> Games</Link>
                        </div>
                    </div>
                </nav>
                <div className="max-w-2xl mx-auto px-4 pt-28 pb-24 text-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="text-6xl mb-4">🔗</div>
                        <h1 className="font-fredoka text-3xl font-bold text-[#1e293b] mb-2">Food Chain Puzzle</h1>
                        <p className="text-[#475569] mb-8">Put Australian food chains in the correct order — from the sun's energy all the way to the apex predator!</p>
                        <motion.button onClick={startGame} whileHover={{ y: -4, x: -4 }} whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn bg-[#ef4444] text-white text-lg flex items-center gap-2 mx-auto" data-testid="start-game">
                            <Play weight="bold" size={22} /> Start
                        </motion.button>
                    </motion.div>
                </div>
            </div>
        );
    }

    if (gameOver) {
        const total = scores.reduce((s, v) => s + v, 0);
        const max = chains.reduce((s, c) => s + c.items.length, 0);
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    className="brutalist-card p-8 max-w-md w-full text-center bg-white" data-testid="game-complete">
                    <div className="text-6xl mb-4">🏆</div>
                    <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">Food Chains Complete!</h2>
                    <p className="text-[#475569] mb-6">{total}/{max} in correct position</p>
                    <div className="p-3 bg-[#fef3c7] rounded-xl mb-6 inline-block">
                        <Coins weight="duotone" size={20} className="text-[#f59e0b] mx-auto" />
                        <p className="font-fredoka text-xl font-bold">{result ? `+${result.tokens_earned}` : '...'} Tokens</p>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={startGame} className="brutalist-btn flex-1 bg-[#ef4444] text-white"><Play weight="bold" size={18} /> Again</button>
                        <Link to="/games" className="brutalist-btn flex-1 bg-white text-[#1e293b] text-center">Games Hub</Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    const chain = chains[currentChain];

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)]">
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b] px-4 py-3">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to="/games" className="text-[#475569] font-semibold flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Games</Link>
                    <span className="font-fredoka font-bold text-[#1e293b]">{chain.habitat} — Chain {currentChain + 1}/{chains.length}</span>
                </div>
            </div>

            <div className="max-w-lg mx-auto px-4 pt-20 pb-8">
                <div className="text-center mb-4">
                    <h2 className="font-fredoka text-xl font-bold text-[#1e293b]">{chain.name}</h2>
                    <p className="text-sm text-[#475569]">Arrange from energy source (top) to apex predator (bottom)</p>
                </div>

                <div className="space-y-2">
                    {order.map((item, i) => {
                        const isCorrect = checked && item.name === chain.items[i].name;
                        const isWrong = checked && item.name !== chain.items[i].name;
                        return (
                            <motion.div key={item.name} layout
                                className={`brutalist-card p-4 flex items-center gap-3 bg-white ${isCorrect ? 'border-[#22c55e] bg-[#22c55e]/5' : isWrong ? 'border-[#ef4444] bg-[#ef4444]/5' : ''}`}
                                data-testid={`chain-item-${i}`}>
                                <span className="text-2xl w-10 text-center">{item.emoji}</span>
                                <div className="flex-1">
                                    <p className="font-bold text-[#1e293b] text-sm">{item.name}</p>
                                    {checked && <p className="text-xs text-[#475569]">{item.description}</p>}
                                </div>
                                {!checked && (
                                    <div className="flex flex-col gap-1">
                                        <button onClick={() => moveItem(i, -1)} disabled={i === 0}
                                            className="p-1 rounded hover:bg-[#f1f5f9] disabled:opacity-20"><ArrowUp weight="bold" size={16} /></button>
                                        <button onClick={() => moveItem(i, 1)} disabled={i === order.length - 1}
                                            className="p-1 rounded hover:bg-[#f1f5f9] disabled:opacity-20"><ArrowDown weight="bold" size={16} /></button>
                                    </div>
                                )}
                                {isCorrect && <Check weight="bold" size={20} className="text-[#22c55e]" />}
                            </motion.div>
                        );
                    })}
                </div>

                {checked && (
                    <div className="mt-4 p-4 bg-[#f0fdf4] border-2 border-[#22c55e] rounded-xl">
                        <p className="text-sm text-[#475569] font-semibold mb-1">Correct order:</p>
                        <p className="text-sm text-[#1e293b]">{chain.items.map(it => `${it.emoji} ${it.name}`).join(' → ')}</p>
                    </div>
                )}

                <div className="text-center mt-6">
                    {!checked ? (
                        <motion.button onClick={handleCheck} whileHover={{ y: -2, x: -2 }}
                            className="brutalist-btn bg-[#22c55e] text-white" data-testid="check-order">
                            <Check weight="bold" size={18} /> Check Order
                        </motion.button>
                    ) : (
                        <motion.button onClick={handleNext} whileHover={{ y: -2, x: -2 }}
                            className="brutalist-btn bg-[#ef4444] text-white" data-testid="next-chain">
                            {currentChain + 1 >= chains.length ? 'See Results' : 'Next Chain →'}
                        </motion.button>
                    )}
                </div>
            </div>
        </div>
    );
}
